import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit2, User, Building2, Phone, Mail, MapPin,
  DollarSign, CreditCard, TrendingUp, ShoppingCart, FileText,
  CheckCircle, AlertCircle, Loader, X, Check, Calendar, Wallet
} from 'lucide-react';
import {
  getClient, getClientDettes, getClientHistorique, updateClient,
} from '../services/api';
import './ClientDetails.css';

const fmt   = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' XOF';
const fdate = (d) => d ? new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

const STATUTS = {
  payee:           { label:'Payée',     color:'#16a34a', bg:'#dcfce7' },
  dette_partielle: { label:'Partielle', color:'#d97706', bg:'#fef3c7' },
  dette_complete:  { label:'Impayée',   color:'#dc2626', bg:'#fee2e2' },
  annulee:         { label:'Annulée',   color:'#64748b', bg:'#f1f5f9' },
};
const MODE_LABEL = {
  especes:'Espèces', wave:'Wave', orange_money:'Orange Money',
  carte_bancaire:'Carte', cheque:'Chèque', avoir:'Avoir', autre:'Autre',
};

export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client,     setClient]     = useState(null);
  const [dettes,     setDettes]     = useState([]);
  const [historique, setHistorique] = useState([]);
  const [paiements,  setPaiements]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [formData, setFormData] = useState({ nom:'', telephone:'', email:'', adresse:'', limiteCredit:'' });

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { loadData(); }, [id]); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    try {
      const [cr, dr, hr] = await Promise.all([
        getClient(id), getClientDettes(id), getClientHistorique(id),
      ]);
      setClient(cr.data.data);
      setDettes(dr.data.data || []);
      // /historique renvoie { ventes: { count, data }, paiements: { count, data } }
      setHistorique(hr.data.ventes?.data || []);
      setPaiements(hr.data.paiements?.data || []);
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const openEdit = () => {
    setFormData({
      nom:         client.nom,
      telephone:   client.telephone,
      email:       client.email   || '',
      adresse:     client.adresse || '',
      limiteCredit:client.limiteCredit != null ? client.limiteCredit : '',
    });
    setShowEdit(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await updateClient(id, formData);
      showToast('success', 'Client modifié avec succès');
      setShowEdit(false);
      loadData();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="cd-loading"><Loader className="cd-spinner" size={26}/><span>Chargement…</span></div>
  );

  if (!client) return (
    <div className="cd-page">
      <div className="cd-not-found">
        <AlertCircle size={32}/>
        <h3>Client introuvable</h3>
        <button className="cd-back-btn" onClick={() => navigate('/clients')}>
          <ArrowLeft size={15}/> Retour aux clients
        </button>
      </div>
    </div>
  );

  const achatsValides = historique.filter(v => v.statut !== 'annulee');
  const totalAchats = achatsValides.reduce((s, v) => s + (v.totalTTC || 0), 0);

  // Produits achetés par le client, toutes ventes confondues (hors ventes annulées).
  // Regroupés par produit : on cumule les quantités et les montants.
  const produitsAchetes = (() => {
    const map = new Map();
    for (const v of achatsValides) {
      for (const d of (v.details || [])) {
        const cle = String(d.produit || d.codeProduit || d.nomProduit || '');
        if (!cle) continue;
        if (!map.has(cle)) {
          map.set(cle, {
            cle,
            codeProduit: d.codeProduit || '',
            nomProduit:  d.nomProduit  || '—',
            quantite: 0,
            montant: 0,
            dernierAchat: null,
          });
        }
        const item = map.get(cle);
        item.quantite += d.quantite  || 0;
        item.montant  += d.sousTotal || 0;
        if (!item.dernierAchat || new Date(v.createdAt) > new Date(item.dernierAchat)) {
          item.dernierAchat = v.createdAt;
        }
        // Compléter la référence/le nom si une ligne plus ancienne les portait
        if (!item.codeProduit && d.codeProduit) item.codeProduit = d.codeProduit;
      }
    }
    return [...map.values()].sort((a, b) => b.montant - a.montant);
  })();

  const totalQuantite = produitsAchetes.reduce((s, p) => s + p.quantite, 0);
  const totalVerse  = paiements.reduce((s, p) => s + (p.montant || 0), 0);
  const dette       = client.detteActuelle || 0;
  const limite      = client.limiteCredit;
  const pctDette    = limite ? Math.min((dette / limite) * 100, 100) : null;
  const detteColor  = !dette ? '#16a34a'
    : limite == null ? '#3b82f6'
    : pctDette >= 80 ? '#dc2626'
    : pctDette >= 50 ? '#d97706'
    : '#3b82f6';
  const isEntreprise = client.type === 'entreprise';

  return (
    <div className="cd-page">

      {/* Toast */}
      {toast && (
        <div className={`cd-toast cd-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="cd-header">
        <div className="cd-header-left">
          <button className="cd-back-btn-header" onClick={() => navigate('/clients')}>
            <ArrowLeft size={16}/>
          </button>
          <div className={`cd-header-av ${isEntreprise ? 'cd-header-av--ent' : ''}`}>
            {isEntreprise ? <Building2 size={18}/> : <User size={18}/>}
          </div>
          <div>
            <h1 className="cd-header-title">{client.nom}</h1>
            <div className="cd-header-meta">
              <span className={`cd-type-badge ${isEntreprise ? 'cd-type-badge--ent' : ''}`}>
                {isEntreprise ? 'Entreprise' : 'Particulier'}
              </span>
              {client.telephone && (
                <span className="cd-header-contact"><Phone size={11}/> {client.telephone}</span>
              )}
              {client.email && (
                <span className="cd-header-contact"><Mail size={11}/> {client.email}</span>
              )}
            </div>
          </div>
        </div>
        <button className="cd-edit-btn" onClick={openEdit}>
          <Edit2 size={15}/> Modifier
        </button>
      </div>

      <div className="cd-content">

        {/* KPI */}
        <div className="cd-kpi-row">
          {[
            { label:'Dette actuelle',  value: fmt(dette),          color: detteColor, bg: dette === 0 ? '#dcfce7' : '#fee2e2', icon: DollarSign  },
            { label:'Limite crédit',   value: limite != null ? fmt(limite) : 'Illimité', color:'#6366f1', bg:'#eef2ff', icon: CreditCard  },
            { label:'Total achats',    value: fmt(totalAchats),    color:'#16a34a', bg:'#dcfce7', icon: TrendingUp  },
            { label:'Total versé',     value: fmt(totalVerse),     color:'#0d9488', bg:'#f0fdfa', icon: Wallet      },
            { label:'Nb transactions', value: historique.length,   color:'#3b82f6', bg:'#eff6ff', icon: ShoppingCart},
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="cd-kpi" style={{ borderTopColor: k.color }}>
                <div className="cd-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="cd-kpi-val">{k.value}</div>
                <div className="cd-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Barre de crédit */}
        {limite != null && (
          <div className="cd-credit-bar-card">
            <div className="cd-credit-bar-head">
              <span>Utilisation du crédit</span>
              <span style={{ color: detteColor, fontWeight:700 }}>{Math.round(pctDette || 0)}%</span>
            </div>
            <div className="cd-credit-bar-track">
              <div className="cd-credit-bar-fill" style={{ width:`${pctDette || 0}%`, background: detteColor }}/>
            </div>
            <div className="cd-credit-bar-foot">
              <span>Dette : <strong style={{color:detteColor}}>{fmt(dette)}</strong></span>
              <span>Disponible : <strong style={{color:'#16a34a'}}>{fmt(Math.max(0, limite - dette))}</strong></span>
              <span>Limite : <strong>{fmt(limite)}</strong></span>
            </div>
          </div>
        )}

        {/* Infos entreprise */}
        {isEntreprise && (client.ninea || client.rccm || client.representant?.nom) && (
          <div className="cd-info-card">
            <div className="cd-info-card-title"><Building2 size={13}/> Informations entreprise</div>
            <div className="cd-info-grid">
              {client.ninea && (
                <div className="cd-info-item"><span>NINEA</span><strong>{client.ninea}</strong></div>
              )}
              {client.rccm && (
                <div className="cd-info-item"><span>RCCM</span><strong>{client.rccm}</strong></div>
              )}
              {client.adresse && (
                <div className="cd-info-item"><span>Adresse</span><strong>{client.adresse}</strong></div>
              )}
              {client.representant?.nom && (
                <div className="cd-info-item">
                  <span>Représentant</span>
                  <strong>
                    {client.representant.prenom} {client.representant.nom}
                    {client.representant.poste && <span className="cd-muted"> — {client.representant.poste}</span>}
                  </strong>
                </div>
              )}
              {client.representant?.telephone && (
                <div className="cd-info-item"><span>Tél. rep.</span><strong>{client.representant.telephone}</strong></div>
              )}
            </div>
          </div>
        )}

        {/* ── Dettes en cours ── */}
        {dettes.length > 0 && (
          <div className="cd-section">
            <div className="cd-section-head">
              <span className="cd-section-title"><FileText size={14}/> Dettes en cours</span>
              <span className="cd-section-count">{dettes.length} facture{dettes.length > 1 ? 's' : ''}</span>
            </div>
            <div className="cd-card">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Date</th>
                    <th>Total TTC</th>
                    <th>Payé</th>
                    <th>Reste dû</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {dettes.map(d => {
                    const reste = (d.totalTTC || 0) - (d.montantPaye || 0);
                    const st = STATUTS[d.statut] || STATUTS.annulee;
                    return (
                      <tr key={d._id}>
                        <td><span className="cd-num">{d.numeroFacture}</span></td>
                        <td className="cd-date"><Calendar size={11}/> {fdate(d.createdAt)}</td>
                        <td className="cd-amount">{fmt(d.totalTTC)}</td>
                        <td className="cd-paid">{fmt(d.montantPaye || 0)}</td>
                        <td><span className="cd-reste">{fmt(reste)}</span></td>
                        <td><span className="cd-badge" style={{ background:st.bg, color:st.color }}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="cd-tfoot">
                    <td colSpan="4" className="cd-tfoot-label">Total dû</td>
                    <td className="cd-reste cd-reste--total">
                      {fmt(dettes.reduce((s, d) => s + ((d.totalTTC||0)-(d.montantPaye||0)), 0))}
                    </td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* ── Acomptes versés ── */}
        <div className="cd-section">
          <div className="cd-section-head">
            <span className="cd-section-title"><Wallet size={14}/> Acomptes versés</span>
            <span className="cd-section-count">{paiements.length} versement{paiements.length > 1 ? 's' : ''}</span>
          </div>

          {paiements.length === 0 ? (
            <div className="cd-empty">
              <Wallet size={28}/>
              <span>Aucun acompte enregistré pour ce client</span>
            </div>
          ) : (
            <div className="cd-card">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>N° Facture</th>
                    <th>Montant</th>
                    <th>Mode</th>
                    <th>Référence</th>
                    <th>Encaissé par</th>
                  </tr>
                </thead>
                <tbody>
                  {paiements.map(p => (
                    <tr key={p._id}>
                      <td className="cd-date"><Calendar size={11}/> {fdate(p.datePaiement)}</td>
                      <td>
                        {p.venteId?.numeroFacture
                          ? <span className="cd-num">{p.venteId.numeroFacture}</span>
                          : <span className="cd-muted">—</span>}
                      </td>
                      <td className="cd-paid">{fmt(p.montant)}</td>
                      <td>
                        <span className="cd-mode-badge">
                          {MODE_LABEL[p.modePaiement] || p.modePaiement || '—'}
                        </span>
                      </td>
                      <td>{p.reference || <span className="cd-muted">—</span>}</td>
                      <td>
                        {p.enregistrePar
                          ? `${p.enregistrePar.prenom || ''} ${p.enregistrePar.nom || ''}`.trim() || '—'
                          : <span className="cd-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="cd-tfoot">
                    <td colSpan="2" className="cd-tfoot-label">Total versé</td>
                    <td className="cd-paid">{fmt(totalVerse)}</td>
                    <td colSpan="3"/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ── Historique des achats : produits achetés par le client ── */}
        <div className="cd-section">
          <div className="cd-section-head">
            <span className="cd-section-title"><ShoppingCart size={14}/> Historique des achats</span>
            <span className="cd-section-count">
              {produitsAchetes.length} produit{produitsAchetes.length > 1 ? 's' : ''}
            </span>
          </div>

          {produitsAchetes.length === 0 ? (
            <div className="cd-empty">
              <ShoppingCart size={28}/>
              <span>Aucun produit acheté par ce client</span>
            </div>
          ) : (
            <div className="cd-card">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Produit</th>
                    <th>Quantité</th>
                    <th>Montant</th>
                    <th>Dernier achat</th>
                  </tr>
                </thead>
                <tbody>
                  {produitsAchetes.map(p => (
                    <tr key={p.cle}>
                      <td>
                        {p.codeProduit
                          ? <span className="cd-prod-ref">{p.codeProduit}</span>
                          : <span className="cd-muted">—</span>}
                      </td>
                      <td><span className="cd-prod-nom">{p.nomProduit}</span></td>
                      <td><span className="cd-prod-qte">{p.quantite}</span></td>
                      <td className="cd-amount">{fmt(p.montant)}</td>
                      <td className="cd-date"><Calendar size={11}/> {fdate(p.dernierAchat)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="cd-tfoot">
                    <td colSpan="2" className="cd-tfoot-label">Total</td>
                    <td><span className="cd-prod-qte">{totalQuantite}</span></td>
                    <td className="cd-amount">{fmt(produitsAchetes.reduce((s, p) => s + p.montant, 0))}</td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal Édition ── */}
      {showEdit && (
        <>
          <div className="cd-overlay" onClick={() => setShowEdit(false)}/>
          <div className="cd-modal">
            <div className="cd-modal-head">
              <div>
                <h2 className="cd-modal-title"><Edit2 size={15}/> Modifier le client</h2>
                <p className="cd-modal-sub">{client.nom}</p>
              </div>
              <button className="cd-modal-close" onClick={() => setShowEdit(false)}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="cd-modal-body">
                <div className="cd-field">
                  <label>Nom complet *</label>
                  <input type="text" required autoFocus
                    value={formData.nom} onChange={e => setFormData({...formData, nom: e.target.value})}/>
                </div>
                <div className="cd-form-row2">
                  <div className="cd-field">
                    <label>Téléphone *</label>
                    <input type="tel" required
                      value={formData.telephone} onChange={e => setFormData({...formData, telephone: e.target.value})}/>
                  </div>
                  <div className="cd-field">
                    <label>Email <span className="cd-opt">(optionnel)</span></label>
                    <input type="email"
                      value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}/>
                  </div>
                </div>
                <div className="cd-field">
                  <label>Adresse <span className="cd-opt">(optionnel)</span></label>
                  <input type="text"
                    value={formData.adresse} onChange={e => setFormData({...formData, adresse: e.target.value})}/>
                </div>
                <div className="cd-field">
                  <label>Limite de crédit (XOF) <span className="cd-opt">— vide = illimité</span></label>
                  <input type="number" min="0" step="1000" placeholder="Laisser vide pour illimité"
                    value={formData.limiteCredit} onChange={e => setFormData({...formData, limiteCredit: e.target.value})}/>
                </div>
              </div>
              <div className="cd-modal-footer">
                <button type="button" className="cd-btn-cancel" onClick={() => setShowEdit(false)}>Annuler</button>
                <button type="submit" className="cd-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="cd-spin"/> Enregistrement…</>
                    : <><Check size={13}/> Enregistrer</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
